import React, { Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AuthPromptRoute from './components/auth/AuthPromptRoute';
import PhoneBrowseRedirect from './components/discover/PhoneBrowseRedirect';
import DocumentTitleReset from './components/layout/DocumentTitleReset';
import Layout from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { AuthPromptProvider, KeyedRouteContent } from './contexts/AuthPromptContext';
import { CartProvider } from './contexts/CartContext';
import { ChatProvider } from './contexts/ChatContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TripSearchProvider } from './contexts/TripSearchContext';
import { WishlistProvider } from './contexts/WishlistContext';
import './styles/global.css';
import './styles/layout.css';

// Lazy-loaded page components — each page is a separate chunk loaded on demand
const HomePage = React.lazy(() => import('./pages/HomePage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const ServiceCategoryPage = React.lazy(() => import('./pages/ServiceCategoryPage'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const OAuthCallbackPage = React.lazy(() => import('./pages/OAuthCallbackPage'));
const PropertyDetailPage = React.lazy(() => import('./pages/PropertyDetailPage'));
const ServiceDetailPage = React.lazy(() => import('./pages/ServiceDetailPage'));
const PackageDetailPage = React.lazy(() => import('./pages/PackageDetailPage'));
const PackagesPage = React.lazy(() => import('./pages/PackagesPage'));
const MyBookingsPage = React.lazy(() => import('./pages/MyBookingsPage'));
const MyRequestsPage = React.lazy(() => import('./pages/MyRequestsPage'));
const RequestPaymentPage = React.lazy(() => import('./pages/RequestPaymentPage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const WishlistPage = React.lazy(() => import('./pages/WishlistPage'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const BlogIndexPage = React.lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const ConciergeEnquiryPage = React.lazy(() => import('./pages/ConciergeEnquiryPage'));
const JoinPage = React.lazy(() => import('./pages/JoinPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const BelhopPlusPage = React.lazy(() => import('./pages/BelhopPlusPage'));
const BelhopPlusApplyPage = React.lazy(() => import('./pages/BelhopPlusApplyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const LegalArchivePage = React.lazy(() => import('./pages/LegalArchivePage'));
const ArchivedLegalVersionPage = React.lazy(() => import('./pages/ArchivedLegalVersionPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const NextPaxPaymentPage = React.lazy(() => import('./pages/NextPaxPaymentPage'));
const NativePaymentReturnPage = React.lazy(() => import('./pages/NativePaymentReturnPage'));
const NativePaymentSuccessPage = React.lazy(() => import('./pages/NativePaymentSuccessPage'));
const BookingConfirmationPage = React.lazy(() => import('./pages/BookingConfirmationPage'));
const BookingDetailPage = React.lazy(() => import('./pages/BookingDetailPage'));
const RepriceApprovalPage = React.lazy(() => import('./pages/RepriceApprovalPage'));
const MultiBookingConfirmationPage = React.lazy(
  () => import('./pages/MultiBookingConfirmationPage'),
);
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const AdminCreateListingPage = React.lazy(() => import('./pages/AdminCreateListingPage'));
const AdminCreateAccommodationPage = React.lazy(
  () => import('./pages/AdminCreateAccommodationPage'),
);
const AdminCreatePackagePage = React.lazy(() => import('./pages/AdminCreatePackagePage'));
const AdminBlogEditorPage = React.lazy(() => import('./pages/AdminBlogEditorPage'));
const AdminTwoFactorPage = React.lazy(() => import('./pages/AdminTwoFactorPage'));
const AdminEditPackagePage = React.lazy(() => import('./pages/AdminEditPackagePage'));
const AdminEditAccommodationPage = React.lazy(() => import('./pages/AdminEditAccommodationPage'));
const AdminEditServicePage = React.lazy(() => import('./pages/AdminEditServicePage'));
const AdminBrandPricingPage = React.lazy(() => import('./pages/AdminBrandPricingPage'));

const PageFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}
  >
    <div className="spinner" />
  </div>
);

const App: React.FC = () => {
  return (
    <>
      <NotificationProvider>
        <CookieConsentProvider>
          <AuthProvider>
            <WishlistProvider>
              <CartProvider>
                <TripSearchProvider>
                  <ChatProvider>
                    <Router>
                      <AuthPromptProvider>
                        <DocumentTitleReset />
                        <Layout>
                          <Suspense fallback={<PageFallback />}>
                            <KeyedRouteContent>
                              <Routes>
                                <Route path="/" element={<DiscoverPage />} />
                                <Route path="/discover" element={<DiscoverPage />} />
                                <Route
                                  path="/accommodations"
                                  element={
                                    <PhoneBrowseRedirect show="stays">
                                      <HomePage defaultContentType="accommodations" />
                                    </PhoneBrowseRedirect>
                                  }
                                />
                                <Route path="/search" element={<SearchResultsPage />} />
                                <Route
                                  path="/services"
                                  element={
                                    <PhoneBrowseRedirect show="services">
                                      <HomePage defaultContentType="services" />
                                    </PhoneBrowseRedirect>
                                  }
                                />
                                <Route
                                  path="/services/category/:category"
                                  element={<ServiceCategoryPage />}
                                />
                                <Route path="/login" element={<AuthPromptRoute entry="login" />} />
                                <Route
                                  path="/register"
                                  element={<AuthPromptRoute entry="register" />}
                                />
                                <Route
                                  path="/forgot-password"
                                  element={<AuthPromptRoute entry="forgotPassword" />}
                                />
                                <Route
                                  path="/verify-email"
                                  element={<Navigate to="/login" replace />}
                                />
                                <Route path="/auth/callback" element={<OAuthCallbackPage />} />
                                <Route
                                  path="/property/:listingRef"
                                  element={<PropertyDetailPage />}
                                />
                                <Route
                                  path="/service/:listingRef"
                                  element={<ServiceDetailPage />}
                                />
                                <Route path="/packages" element={<PackagesPage />} />
                                <Route path="/packages/:slug" element={<PackageDetailPage />} />
                                {/* Chat is handled by the ConciergePanel in Layout */}
                                <Route
                                  path="/profile"
                                  element={
                                    <ProtectedRoute>
                                      <UserProfilePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/wishlist"
                                  element={
                                    <ProtectedRoute>
                                      <WishlistPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/support" element={<SupportPage />} />
                                {/* Deliberately public — no ProtectedRoute. Name and
                              email prefill from the auth context when signed
                              in, but the endpoint itself is unauthenticated. */}
                                <Route
                                  path="/concierge-enquiry"
                                  element={<ConciergeEnquiryPage />}
                                />
                                <Route path="/blog" element={<BlogIndexPage />} />
                                <Route path="/blog/:slug" element={<BlogPostPage />} />
                                {/* Deliberately public — no ProtectedRoute. Supply-side
                              lead capture; name and email prefill from the auth
                              context when signed in, but the endpoint itself is
                              unauthenticated. */}
                                <Route path="/join" element={<JoinPage />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/belhop-plus" element={<BelhopPlusPage />} />
                                {/* Deliberately public — no ProtectedRoute. The Belhop+
                              membership application funnel; sales-led, no payment
                              collected here. */}
                                <Route
                                  path="/belhop-plus/apply"
                                  element={<BelhopPlusApplyPage />}
                                />
                                <Route path="/terms" element={<TermsPage />} />
                                <Route
                                  path="/terms/archive"
                                  element={<LegalArchivePage policy="terms" />}
                                />
                                <Route
                                  path="/terms/archive/:version"
                                  element={<ArchivedLegalVersionPage policy="terms" />}
                                />
                                <Route path="/privacy" element={<PrivacyPage />} />
                                <Route
                                  path="/privacy/archive"
                                  element={<LegalArchivePage policy="privacy" />}
                                />
                                <Route
                                  path="/privacy/archive/:version"
                                  element={<ArchivedLegalVersionPage policy="privacy" />}
                                />
                                {/* Cart is reachable by guests (matches the dropdown,
                              available without login). Checkout still gates auth. */}
                                <Route path="/cart" element={<CartPage />} />
                                <Route
                                  path="/bookings"
                                  element={
                                    <ProtectedRoute>
                                      <ErrorBoundary>
                                        <MyBookingsPage />
                                      </ErrorBoundary>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/requests"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <ErrorBoundary>
                                        <MyRequestsPage />
                                      </ErrorBoundary>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/requests/groups/:groupId/pay"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <RequestPaymentPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/requests/:id/pay"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <RequestPaymentPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/checkout"
                                  element={
                                    <ProtectedRoute>
                                      <CheckoutPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/checkout/payment/:draftId"
                                  element={
                                    <ProtectedRoute>
                                      <NextPaxPaymentPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking-confirmation/:bookingId"
                                  element={
                                    <ProtectedRoute>
                                      <BookingConfirmationPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking/:id"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <BookingDetailPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking/:id/approve-charge"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <RepriceApprovalPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking/:id/payment"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <NativePaymentReturnPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking/:id/success"
                                  element={
                                    <ProtectedRoute requireAuth={true}>
                                      <NativePaymentSuccessPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking-confirmation/multi"
                                  element={
                                    <ProtectedRoute>
                                      <MultiBookingConfirmationPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/property/edit/:id"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminEditAccommodationPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/service/edit/:id"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminEditServicePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <Navigate replace to="/admin/bookings" />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/two-factor"
                                  element={
                                    <ProtectedRoute
                                      requireAdmin={true}
                                      allowWithoutTwoFactor={true}
                                    >
                                      <AdminTwoFactorPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/listings/new"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminCreateListingPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/accommodations/new"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminCreateAccommodationPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/packages/new"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminCreatePackagePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/packages/edit/:id"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminEditPackagePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/blog/:postId"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminBlogEditorPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/brands/:id/pricing"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminBrandPricingPage />
                                    </ProtectedRoute>
                                  }
                                />
                                {/* Deep link from the admin-notification emails (reprice
                              consent, guest cancellation, booking-created) —
                              AdminPage reads the :bookingId param and opens that
                              booking's details on the bookings surface. */}
                                <Route
                                  path="/admin/bookings/:bookingId"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/:tab"
                                  element={
                                    <ProtectedRoute requireAdmin={true}>
                                      <AdminPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="*" element={<NotFoundPage />} />
                              </Routes>
                            </KeyedRouteContent>
                          </Suspense>
                        </Layout>
                      </AuthPromptProvider>
                    </Router>
                  </ChatProvider>
                </TripSearchProvider>
              </CartProvider>
            </WishlistProvider>
          </AuthProvider>
        </CookieConsentProvider>
      </NotificationProvider>
    </>
  );
};

export default App;
